import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { QuoteItemBaqData, useBulkUpdateComponents } from '@frontend/api';
import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalButtonGroup,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@frontend/components';

import { TeamMemberSelect } from 'modules/common/smartComponents/TeamMemberSelect';
import { broadcastQuoteItemWorkflowChange } from 'modules/quotes/quoteDetailsPage/hooks/useSyncQpvAndBaqTabs';
import { quoteItemSelect } from 'modules/quotes/quoteItemNav/data/actions';
import {
  receiveQuoteItems,
  refreshComponent,
  updateComponent,
} from 'modules/quotes/quoteItems/data/actions';
import {
  getExpandedQuoteItemId,
  getAreQuoteItemsReadOnly,
  getQuoteItemsByIdsDenormalized,
} from 'modules/quotes/quoteItems/data/selectors';

import quoteService from 'services/quoteService';

import { getQuoteId } from 'store/quotes/selectors';

import { notifyError } from 'utils/notifyUtils';

import * as actions from './data/actions';
import { getIsOpen, getQuoteItemIds } from './data/reducer';

export const BulkEditEstimatorModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getIsOpen);
  const quoteItemIds = useSelector(getQuoteItemIds);
  const quoteId = useSelector(getQuoteId);

  const quoteItems: QuoteItemBaqData[] = useSelector((state) =>
    getQuoteItemsByIdsDenormalized(state, quoteItemIds),
  );

  const expandedQuoteItemId = useSelector(getExpandedQuoteItemId);

  const refreshComponentId = useMemo(() => {
    return (
      expandedQuoteItemId &&
      quoteItems.find((qi) => qi.id === expandedQuoteItemId)?.rootComponent?.id
    );
  }, [expandedQuoteItemId, quoteItems]);

  const quoteItemsExportControlled: boolean =
    quoteItems.some((qi) => qi.exportControlled) || false;

  const defaultEstimatorId = useMemo(() => {
    const firstEstimatorId =
      quoteItems.length > 0
        ? quoteItems[0]?.rootComponent?.estimator?.id
        : null;
    return quoteItems.every(
      (qi) => qi?.rootComponent?.estimator?.id === firstEstimatorId,
    )
      ? firstEstimatorId
      : null;
  }, [quoteItems]);

  const componentIds = quoteItems.map((qi) => qi?.rootComponent?.id);

  const areQuoteItemsReadOnly = useSelector((state) =>
    getAreQuoteItemsReadOnly(state, quoteItemIds),
  );

  const estimatorRef = useRef<any>(null); // using any because react-select types aren't good until v5

  const onClose = () => dispatch(actions.close());

  const { mutate: bulkUpdateComponents } = useBulkUpdateComponents({
    queryOptions: {
      onSuccess: async () => {
        dispatch(quoteItemSelect.deselectAll());
        if (refreshComponentId) {
          dispatch(
            refreshComponent({ componentId: refreshComponentId, params: {} }),
          );
        }

        const quoteItems = await quoteService.getQuoteItemHeadersById(quoteId);
        dispatch(receiveQuoteItems(quoteItems));

        for (const quoteItem of quoteItems) {
          broadcastQuoteItemWorkflowChange({
            context: {
              quoteItemId: quoteItem.id,
              isRootComponent: true,
            },
          });
        }

        onClose();
      },
      onError: () => {
        notifyError("Couldn't update estimator");
      },
    },
  });

  const onSubmit = () => {
    const componentId = componentIds?.[0];
    dispatch(
      updateComponent.request({
        componentId,
        data: {
          estimatorId:
            typeof estimatorRef.current?.state.value?.value === 'number'
              ? estimatorRef.current?.state.value?.value
              : null,
        },
      }),
    );
    onClose();
  };

  const onSubmitBulk = useCallback(
    () =>
      bulkUpdateComponents({
        quoteId,
        componentIds: componentIds,
        estimatorId:
          typeof estimatorRef.current?.state.value?.value === 'number'
            ? estimatorRef.current?.state.value?.value
            : null,
        updateQuoteEstimator: false,
      }),
    [bulkUpdateComponents, quoteId, componentIds],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalContent
        as="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (componentIds.length === 1) {
            onSubmit();
          } else {
            onSubmitBulk();
          }
        }}
      >
        <ModalHeader>
          Update Estimator Assignment
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <FormControl>
            <FormLabel>Estimator</FormLabel>
            <TeamMemberSelect
              mode="assignment"
              ref={estimatorRef}
              isExportControlled={quoteItemsExportControlled}
              defaultValue={defaultEstimatorId}
              isDisabled={areQuoteItemsReadOnly}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ModalButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </ModalButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
